import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  NgZone,
  viewChild,
} from '@angular/core';

@Component({
  selector: 'app-truncated-text-marquee',
  template: `<span #inner class="truncated-text-marquee-inner">{{ text() }}</span>`,
  styleUrl: './truncated-text-marquee.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TruncatedTextMarqueeComponent {
  private readonly host_ref = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly ng_zone = inject(NgZone);
  private readonly inner_ref = viewChild<ElementRef<HTMLElement>>('inner');

  readonly text = input.required<string>();

  private link: HTMLElement | null = null;
  private animation: Animation | null = null;
  private wait_timeout_id: ReturnType<typeof setTimeout> | null = null;
  private wait_resolve: (() => void) | null = null;
  private is_hovering = false;
  private loop_token = 0;

  constructor() {
    afterNextRender(() => {
      this.link = this.host_ref.nativeElement.closest('a');
      if (this.link === null) {
        return;
      }

      const on_enter = (): void => this.start();
      const on_leave = (): void => this.stop();
      this.ng_zone.runOutsideAngular(() => {
        this.link?.addEventListener('mouseenter', on_enter);
        this.link?.addEventListener('mouseleave', on_leave);
      });
      this.destroy_ref.onDestroy(() => {
        this.link?.removeEventListener('mouseenter', on_enter);
        this.link?.removeEventListener('mouseleave', on_leave);
        this.stop();
      });
    });
  }

  private start(): void {
    this.stop();

    const overflow_px = this.measureOverflow();
    if (overflow_px <= 0) {
      return;
    }

    this.is_hovering = true;
    this.loop_token += 1;
    this.host_ref.nativeElement.classList.add('is-marquee-running');
    void this.runLoop(overflow_px, this.loop_token);
  }

  private stop(): void {
    this.is_hovering = false;
    this.loop_token += 1;
    this.animation?.cancel();
    this.animation = null;
    if (this.wait_timeout_id !== null) {
      clearTimeout(this.wait_timeout_id);
      this.wait_timeout_id = null;
    }
    this.wait_resolve?.();
    this.wait_resolve = null;

    const inner = this.inner_ref()?.nativeElement;
    if (inner) {
      inner.style.transform = '';
    }
    this.host_ref.nativeElement.classList.remove('is-marquee-running');
  }

  private async runLoop(overflow_px: number, token: number): Promise<void> {
    const inner = this.inner_ref()?.nativeElement;
    if (!inner) {
      return;
    }

    const slide_ms = (overflow_px / MARQUEE_PIXELS_PER_SECOND) * 1000;

    while (this.is_hovering && this.loop_token === token) {
      await this.slideToEnd(inner, overflow_px, slide_ms);
      if (this.loop_token !== token) {
        return;
      }

      await this.wait(MARQUEE_PAUSE_MS);
      if (this.loop_token !== token) {
        return;
      }

      this.animation?.cancel();
      this.animation = null;
      inner.style.transform = 'translateX(0)';

      await this.wait(MARQUEE_PAUSE_MS);
    }
  }

  private measureOverflow(): number {
    const inner = this.inner_ref()?.nativeElement;
    if (!inner) {
      return 0;
    }

    return inner.scrollWidth - this.host_ref.nativeElement.clientWidth;
  }

  private slideToEnd(inner: HTMLElement, overflow_px: number, duration_ms: number): Promise<void> {
    return new Promise(resolve => {
      this.animation?.cancel();
      inner.style.transform = '';
      this.animation = inner.animate([{ transform: 'translateX(0)' }, { transform: `translateX(-${overflow_px}px)` }], {
        duration: duration_ms,
        easing: 'linear',
        fill: 'forwards',
      });
      this.animation.addEventListener('finish', () => resolve(), { once: true });
      this.animation.addEventListener('cancel', () => resolve(), { once: true });
    });
  }

  private wait(ms: number): Promise<void> {
    return new Promise(resolve => {
      this.wait_resolve = resolve;
      this.wait_timeout_id = setTimeout(() => {
        this.wait_timeout_id = null;
        this.wait_resolve = null;
        resolve();
      }, ms);
    });
  }
}

const MARQUEE_PIXELS_PER_SECOND = 50;
const MARQUEE_PAUSE_MS = 2000;
